/**
 * Copyright (c) 2020
 *
 * Contains the representation of a Component
 *
 * @summary component class definition
 * @author Julien <julien.donnadieu@yooda.com>
 *
 * Created at     : 2021-08-17 10:21:56
 * Last modified  : 2021-08-17 16:17:44
 */

import ProductFamily from '@/models/customersManager/entity/ProductFamily.js'
import SubscriptionComponent from '@/models/customersManager/entity/SubscriptionComponent.js'
import ComponentKindEnumType from '@/models/customersManager/enum/ComponentKindEnumType.js'

class Component {

    /**
     * @type id Integer
     */
    #id

    /**
     * @type chargifyId Integer
     */
    #chargifyId

    /**
     * @type name String
     */
    #name

    /**
     * @type description null|String
     */
    #description

    /**
     * @type unitName String
     */
    #unitName

    /**
     * @type kind String
     */
    #kind

    /**
     * @type createdAt Date
     */
    #createdAt

    /**
     * @type archived Boolean
     */
    #archived

    /**
     * @type handle null|String
     */
    #handle

    /**
     * @type productFamily null|ProductFamily
     */
    #productFamily = null

    /**
     * @type subscriptionComponents SubscriptionComponent[]
     */
    #subscriptionComponents = []

    /**
     * @param rawComponent Object
     */
    constructor(rawComponent = null) {
        if (rawComponent !== null) {
            const {
                id,
                chargifyId,
                name,
                description,
                unitName,
                kind,
                createdAt,
                archived,
                handle,
                productFamily,
                subscriptionComponents
            } = rawComponent
            this.id = id
            this.chargifyId = chargifyId
            this.name = name
            this.description = description
            this.unitName = unitName
            this.kind = kind
            this.createdAt = createdAt
            this.archived = archived
            this.handle = handle
            this.productFamily = productFamily
            this.subscriptionComponents = subscriptionComponents
        }
    }

    /**
     * @returns Integer
     */
    get id () {
        return this.#id
    }

    /**
     * @param id String
     * @returns void
     */
    set id (id) {
        this.#id = parseInt(id)
    }

    /**
     * @returns Integer
     */
    get chargifyId () {
        return this.#chargifyId
    }

    /**
     * @param chargifyId String
     * @returns void
     */
    set chargifyId (chargifyId) {
        this.#chargifyId = parseInt(chargifyId)
    }

    /**
     * @returns String
     */
    get name () {
        return this.#name
    }

    /**
     * @param name String
     * @returns void
     */
    set name (name) {
        this.#name = name
    }

    /**
     * @returns String
     */
    get description () {
        return this.#description
    }

    /**
     * @param description String
     * @returns void
     */
    set description (description) {
        this.#description = description
    }

    /**
     * @returns String
     */
    get unitName () {
        return this.#unitName
    }

    /**
     * @param unitName String
     * @returns void
     */
    set unitName (unitName) {
        this.#unitName = unitName
    }

    /**
     * @returns null|String
     */
    get kind () {
        return this.#kind
    }

    /**
     * @param kind null|String
     * @returns void
     */
    set kind (kind) {
        if (!Object.values(ComponentKindEnumType).includes(kind)) {
            throw new Error('kind is not defined')
        }
        this.#kind = kind
    }

    /**
     * @returns Date
     */
    get createdAt () {
        return this.#createdAt
    }

    /**
     * @param createdAt String
     * @returns void
     */
    set createdAt (createdAt) {
        this.#createdAt = new Date(createdAt)
    }

    /**
     * @returns Boolean
     */
    get archived () {
        return this.#archived
    }

    /**
     * @param archived Boolean
     * @returns void
     */
    set archived (archived) {
        this.#archived = archived
    }

    /**
     * @returns null|String
     */
    get handle () {
        return this.#handle
    }

    /**
     * @param handle null|String
     * @returns void
     */
    set handle (handle) {
        this.#handle = handle
    }

    /**
     * @returns ProductFamily|null
     */
    get productFamily () {
        return this.#productFamily
    }

    /**
     * @param rawProductFamily null|Object
     * @returns void
     */
    set productFamily (rawProductFamily) {
        this.#productFamily = null
        if (rawProductFamily !== null && typeof rawProductFamily !== 'undefined') {
            this.#productFamily = new ProductFamily(rawProductFamily)
        }
    }

    /**
     * @returns SubscriptionComponents[]|null
     */
    get subscriptionComponents () {
        return this.#subscriptionComponents
    }

    /**
     * @param rawSubscriptionComponents null|Object[]
     * @returns void
     */
    set subscriptionComponents (rawSubscriptionComponents) {
        this.#subscriptionComponents = []
        if (rawSubscriptionComponents !== null && typeof rawSubscriptionComponents !== 'undefined') {
            rawSubscriptionComponents.forEach((rawSubscriptionComponent) => {
                const subscriptionComponent = new SubscriptionComponent(rawSubscriptionComponent)
                this.#subscriptionComponents = [...this.#subscriptionComponents, subscriptionComponent]
            });
        }
    }
}

export default Component
